/**
 * The pre-filled "answer sheet" — how the Action Plan fills in government forms (owner, 3 Oct 2026).
 *
 * WHY NOT THE OFFICIAL PDF. Services Australia's SA485, SA457 and SA456 are print-and-fill PDFs with
 * no fillable fields, and they are locked against modification, so answers cannot be stamped into
 * the official document. They are also revised several times a year (SA485.2511 → .2607), so a
 * copy kept here would go stale. What the client gets instead is the official form's own questions,
 * by number, with the answer from their profile beside each one — to copy onto the paper form, or
 * to have open while they complete the same claim online through myGov, which Services Australia
 * says is faster. Every sheet links to the form's landing page, never to a PDF address (those
 * change with each edition).
 *
 * WHAT EACH ROW SAYS. `filled` — taken from the profile. `check` — from the profile but an
 * estimate the form wants exact (a balance on a statement, a market value). `you` — the form asks
 * for something the profile does not hold (a reference number, an address, a legal description).
 * `skip` — the form's own "go to" logic means the question does not apply. Nothing is invented: a
 * figure the profile does not have is a `you`, never a guess.
 *
 * No provider names in client-facing text; the documents are the client's, not ours.
 */
import { formatCurrency } from '../../utils/currency.ts';

export type AnswerStatus = 'filled' | 'check' | 'you' | 'skip';

export interface AnswerRow {
    /** The question number on the official form, as printed; omitted where the form does not number it. */
    q?: string;
    question: string;
    answer: string;
    status: AnswerStatus;
    note?: string;
}

export interface AnswerSection {
    title: string;
    rows: AnswerRow[];
}

export interface AnswerSheet {
    /** e.g. 'SA485' — shown as the badge and in the file name. */
    code: string;
    title: string;
    /** The official landing page for the form (never a PDF address). */
    officialUrl: string;
    /** Who the sheet is for, e.g. "Margaret Example". */
    personName: string;
    /** One or two sentences: what the form is for and why this client is completing it. */
    intro: string;
    /** How to lodge — online first where the form allows it. */
    howToLodge: string[];
    sections: AnswerSection[];
    /** The documents the form asks to be attached, worked out from the answers. */
    documents: string[];
    /** Checklist-style documents (no question numbers) render their rows as tick boxes. */
    kind?: 'form' | 'checklist';
    generatedAt: string;
}

export const money = (v: number): string => formatCurrency(Math.round(v));

export function escapeHtml(s: unknown): string {
    return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}

/** DD/MM/YYYY, as the forms ask. Blank when the date is missing or unreadable. */
export function formDate(iso: string | undefined | null): string {
    if (!iso) return '';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const STATUS_LABEL: Record<AnswerStatus, string> = {
    filled: 'From your profile',
    check: 'Check this',
    you: 'You fill this in',
    skip: 'Does not apply',
};

/** How many rows of each kind — the summary line at the top of the sheet. */
export function statusCounts(sheet: AnswerSheet): Record<AnswerStatus, number> {
    const counts: Record<AnswerStatus, number> = { filled: 0, check: 0, you: 0, skip: 0 };
    for (const s of sheet.sections) for (const r of s.rows) counts[r.status] += 1;
    return counts;
}

export function buildAnswerSheetHtml(sheet: AnswerSheet): string {
    const counts = statusCounts(sheet);
    const isChecklist = sheet.kind === 'checklist';
    const generated = new Date(sheet.generatedAt);
    const when = Number.isNaN(generated.getTime()) ? '' : generated.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
    const section = (s: AnswerSection) => isChecklist
        ? `<h2>${escapeHtml(s.title)}</h2><ul class="check">${s.rows.map((r) => `<li><span class="box" aria-hidden="true"></span><div><strong>${escapeHtml(r.question)}</strong>${r.answer ? `<div class="sub">${escapeHtml(r.answer)}</div>` : ''}${r.note ? `<div class="note-inline">${escapeHtml(r.note)}</div>` : ''}</div></li>`).join('')}</ul>`
        : `<h2>${escapeHtml(s.title)}</h2><table><colgroup><col class="c-q"><col class="c-ask"><col></colgroup><thead><tr><th class="qn">Q</th><th>The form asks</th><th>Your answer</th></tr></thead><tbody>${s.rows.map((r) => `<tr class="st-${r.status}"><td class="qn">${escapeHtml(r.q ?? '')}</td><td>${escapeHtml(r.question)}</td><td><div class="ans">${escapeHtml(r.answer)}</div><div class="tag tag-${r.status}">${STATUS_LABEL[r.status]}</div>${r.note ? `<div class="note-inline">${escapeHtml(r.note)}</div>` : ''}</td></tr>`).join('')}</tbody></table>`;

    return `<!DOCTYPE html>
<html lang="en-AU"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(sheet.code ? `${sheet.code} — ${sheet.title}` : sheet.title)}</title>
<style>
  :root{color-scheme:light}
  *{box-sizing:border-box}
  body{margin:0;background:#f8fafc;color:#0f172a;font:15px/1.5 -apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif}
  .page{max-width:900px;margin:0 auto;padding:36px 20px 64px}
  .doc{background:#fff;border:1px solid #e2e8f0;border-radius:16px;box-shadow:0 10px 30px rgba(15,23,42,.06);overflow:hidden}
  .doc::before{content:'';display:block;height:4px;background:linear-gradient(90deg,#2563eb,#14b8a6)}
  .inner{padding:32px 36px 36px}
  .badge{display:inline-block;font-size:12px;font-weight:800;letter-spacing:.06em;color:#1d4ed8;border:1px solid #bfdbfe;border-radius:999px;padding:3px 10px;margin-bottom:10px}
  h1{margin:0 0 4px;font-size:25px;letter-spacing:-.02em;line-height:1.2}
  .for{color:#475569;margin:0 0 16px}
  .lede{color:#334155;margin:0 0 14px}
  .note{border:1px solid #e2e8f0;border-left:4px solid #2563eb;border-radius:12px;padding:12px 14px;margin:14px 0;font-size:14px;color:#334155}
  .note--amber{border-left-color:#f59e0b}
  .note ul{margin:6px 0 0;padding-left:18px}
  .summary{display:flex;flex-wrap:wrap;gap:8px;margin:6px 0 4px}
  .summary span{font-size:12.5px;font-weight:600;border:1px solid #e2e8f0;border-radius:999px;padding:3px 10px;color:#334155;background:#fff}
  h2{font-size:12px;font-weight:800;text-transform:uppercase;letter-spacing:.08em;color:#64748b;margin:26px 0 8px}
  table{width:100%;border-collapse:collapse;table-layout:fixed}
  col.c-q{width:48px}col.c-ask{width:46%}
  th,td{text-align:left;vertical-align:top;padding:9px 10px;border-bottom:1px solid #e2e8f0;font-size:14px}
  th{font-size:11.5px;text-transform:uppercase;letter-spacing:.05em;color:#64748b;font-weight:700}
  td.qn,th.qn{color:#1d4ed8;font-weight:800;font-variant-numeric:tabular-nums}
  .ans{font-weight:600;color:#0f172a;white-space:pre-line}
  tr.st-you .ans{color:#92400e}
  tr.st-skip td{color:#94a3b8}
  tr.st-skip .ans{color:#94a3b8;font-weight:500}
  .tag{display:inline-block;margin-top:4px;font-size:11px;font-weight:700;border-radius:999px;padding:1px 8px;border:1px solid #e2e8f0;color:#475569}
  .tag-filled{border-color:#a7f3d0;color:#047857}
  .tag-check{border-color:#bfdbfe;color:#1d4ed8}
  .tag-you{border-color:#fcd34d;color:#92400e}
  .tag-skip{color:#94a3b8}
  .note-inline{margin-top:4px;font-size:12.5px;color:#64748b}
  ul.check{list-style:none;margin:0;padding:0}
  ul.check li{display:flex;gap:12px;align-items:flex-start;padding:9px 0;border-bottom:1px solid #f1f5f9}
  .box{flex:0 0 18px;height:18px;border:2px solid #94a3b8;border-radius:4px;margin-top:2px}
  .sub{font-size:13.5px;color:#475569}
  ol.docs{margin:0;padding-left:20px}
  ol.docs li{margin:0 0 5px}
  .foot{margin-top:26px;padding-top:12px;border-top:1px solid #e2e8f0;font-size:12.5px;color:#64748b}
  .actions{display:flex;gap:10px;justify-content:flex-end;margin:0 0 12px}
  .btn{border:1px solid #cbd5e1;background:#fff;border-radius:10px;padding:8px 14px;font-weight:600;font-size:14px;cursor:pointer;color:#0f172a;text-decoration:none}
  @media print{body{background:#fff}.page{padding:0}.doc{border:0;box-shadow:none;border-radius:0}.actions{display:none}tr{break-inside:avoid}}
</style></head>
<body><div class="page">
<div class="actions">${sheet.officialUrl ? `<a class="btn" href="${escapeHtml(sheet.officialUrl)}" target="_blank" rel="noopener noreferrer">Open the official ${isChecklist ? 'page' : 'form'}</a>` : ''}<button class="btn" type="button" onclick="window.print()">Print or save as PDF</button></div>
<div class="doc"><div class="inner">
${sheet.code ? `<div class="badge">${escapeHtml(sheet.code)}</div>` : ''}
<h1>${escapeHtml(sheet.title)}</h1>
<p class="for">Prepared for ${escapeHtml(sheet.personName)}${when ? ` on ${escapeHtml(when)}` : ''}</p>
<p class="lede">${escapeHtml(sheet.intro)}</p>
${isChecklist ? '' : `<div class="summary"><span>${counts.filled + counts.check} answered from your profile</span><span>${counts.you} for you to fill in</span>${counts.skip ? `<span>${counts.skip} that do not apply</span>` : ''}</div>
<div class="note">This is not the form itself. ${sheet.code ? `It follows the official ${escapeHtml(sheet.code)} question by question` : 'It follows the official form'}, with the answer from your profile beside each one. Copy the answers onto the form, or keep this open while you complete it online. Answer as at the date you lodge: if anything has changed since you last updated your profile, use today's figure.</div>`}
${sheet.howToLodge.length ? `<div class="note note--amber"><strong>${isChecklist ? 'Before you start' : 'How to lodge'}</strong><ul>${sheet.howToLodge.map((l) => `<li>${escapeHtml(l)}</li>`).join('')}</ul></div>` : ''}
${sheet.sections.map(section).join('')}
${sheet.documents.length ? `<h2>Documents to include</h2><ol class="docs">${sheet.documents.map((d) => `<li>${escapeHtml(d)}</li>`).join('')}</ol>` : ''}
<div class="foot">Prepared from the details in your profile${sheet.code ? `. The official form may have changed since: if a question number does not match, go by the wording` : ''}. This is general information to help you complete it, not advice; Services Australia makes the assessment.</div>
</div></div>
</div></body></html>`;
}

/** Opens the sheet in a new tab and downloads a copy, the way every document in the app is opened. */
export function openAnswerSheet(sheet: AnswerSheet, fileStem: string): void {
    const html = buildAnswerSheetHtml(sheet);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const tab = window.open('', '_blank');
    if (tab) {
        tab.document.open();
        tab.document.write(html);
        tab.document.close();
    } else {
        const openLink = document.createElement('a');
        openLink.href = url;
        openLink.target = '_blank';
        openLink.rel = 'noopener noreferrer';
        document.body.appendChild(openLink);
        openLink.click();
        document.body.removeChild(openLink);
    }
    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.download = `${fileStem.replace(/[^a-zA-Z0-9_-]/g, '_')}_${sheet.generatedAt.slice(0, 10)}.html`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    setTimeout(() => URL.revokeObjectURL(url), 30000);
}
